import {
  keepPreviousData,
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type Action, can } from '~/lib/permissions';
import { useToast } from '~/ui/Toast';
import { useApi } from './ApiProvider';
import type { AuditFilter, ClientListFilter, Person } from './types';
import { ApiError, type WorkspaceApi } from './WorkspaceApi';

/** Query helpers: one per view, keyed so ServerEvents can make them stale (see keysFor). */
function useView<T>(key: QueryKey, fn: (api: WorkspaceApi) => Promise<T>, enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: key, queryFn: () => fn(api), enabled });
}

export const useSession = () => useView(['session'], (a) => a.me());
export const useHome = () => useView(['home'], (a) => a.home());
export const usePeopleQuery = () => useView(['people'], (a) => a.people());
export const useClients = (filter: ClientListFilter) =>
  useView(['clients', filter], (a) => a.listClients(filter));
/** The best `limit` clients for what's typed (GET /clients?q=); the last results stay while the next load. */
export function useClientSearch(q: string, limit: number) {
  const api = useApi();
  return useQuery({
    queryKey: ['clients', 'search', q, limit],
    queryFn: () => api.listClients('all', { q, limit }),
    enabled: q.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
export const useClientFile = (id: string | null) =>
  useView(['clientFile', id], (a) => a.clientFile(id ?? ''), id != null);
export const useYears = (clientId: string | null) =>
  useView(['years', clientId], (a) => a.listYears(clientId ?? ''), Boolean(clientId));
export const useYear = (taxYearId: string | null) =>
  useView(['year', taxYearId], (a) => a.year(taxYearId ?? ''), taxYearId != null);
export const useNeeds = (taxYearId: string) =>
  useView(['needs', taxYearId], (a) => a.needs(taxYearId));
export const useDocument = (id: string | null) =>
  useView(['document', id], (a) => a.document(id ?? ''), id != null);
export const useQuestion = (id: string | null) =>
  useView(['question', id], (a) => a.question(id ?? ''), id != null);
export const useQuestions = () => useView(['questions'], (a) => a.questions());
export const useDeliverable = (id: string | null) =>
  useView(['deliverable', id], (a) => a.deliverable(id ?? ''), id != null);
export const useConversations = () => useView(['conversations'], (a) => a.conversations());
export const useConversation = (id: string | null) =>
  useView(['conversation', id], (a) => a.conversation(id ?? ''), id != null);
export const useMessages = (id: string | null) =>
  useView(['messages', id], (a) => a.messages(id ?? ''), id != null);
export const useRun = (id: string | null) =>
  useView(['run', id], (a) => a.run(id ?? ''), id != null);
export const useUsageMe = () => useView(['usageMe'], (a) => a.usageMe());
export const useUsageFirm = () => useView(['usageFirm'], (a) => a.usageFirm());
export const useFirmSettings = () => useView(['settings'], (a) => a.firmSettings());
export const useUsers = () => useView(['users'], (a) => a.users());
export const useIntegrations = () => useView(['integrations'], (a) => a.integrations());
export const useEntra = () => useView(['entra'], (a) => a.entra());
export const useAgents = () => useView(['agents'], (a) => a.agentsConfig());
export const useBreakGlassResets = () => useView(['breakGlassResets'], (a) => a.breakGlassResets());
export const useAllowanceRequests = () =>
  useView(['allowanceRequests'], (a) => a.allowanceRequests());
export const useAudit = (filter: AuditFilter) => useView(['audit', filter], (a) => a.audit(filter));
export const useSetupStatus = () => useView(['setupStatus'], (a) => a.setupStatus());

/** Name, initials and title for anyone at the firm. */
export function usePeople(): (id: string | null | undefined) => Person | null {
  const { data } = usePeopleQuery();
  return (id) => (id ? (data?.find((p) => p.id === id) ?? null) : null);
}

/**
 * A state change. Runs `fn` against the api, then refreshes every mounted view (the server
 * may have changed several: a filed document ticks a needs list, a lock touches a year).
 * Failures surface as a toast in the interface's voice; the caller can also read `error`.
 */
export function useAction<A, R>(
  fn: (api: WorkspaceApi, args: A) => Promise<R>,
  options: { quiet?: boolean } = {},
) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (args: A) => fn(api, args),
    onSettled: () => qc.invalidateQueries(),
    onError: (error) => {
      if (options.quiet) return;
      toast.show(
        error instanceof ApiError ? error.message : 'That didn’t work. Try again.',
        'error',
      );
    },
  });
}

/** `can` (lib/permissions) for the signed-in person: what this screen should offer them. */
export function useCan(): (action: Action) => boolean {
  const user = useSession().data?.user;
  return (action) => can(user, action);
}
